// Voice mode on OpenAI's voice model — T67 (13 Sep 2026), agents doc § 7.4: kadmon's ears and mouth.
//
// THE MIDDLE PATH, Natanel's decision after hearing T66a's pipeline loop (13 Sep 2026): a speech-
// to-speech model hears the recruiter and speaks to them — the turn-taking, the interruptions and
// the voice are its — and Claude stays kadmon's brain. The model knows nothing about people; every
// real request reaches console-vera through one tool, `ask_vera`, answered HERE in the browser by
// the panel's own ask (the streamed find, the same chat row, the same cards), and the model reads
// her answer back. A card that stays waits for a tap on screen: nothing spoken applies it (T595).
// The script the model runs under is `@vera/agent/find`'s VOICE_INSTRUCTIONS; the BFF fixes it,
// the tools and the voice at minting, and the browser cannot change them.
//
// HOW IT CONNECTS. `POST /voice/session` mints a ten-minute key; the browser opens a WebRTC call
// to OpenAI itself with it — the microphone out, her voice in, a data channel for events — so the
// brain tier holds no socket and carries no audio, as with Transcribe (src/transcribe.ts).
//
// QUICKER TO OPEN, QUICKER TO ANSWER (16 Sep 2026, Natanel: less wait between Talk and talking, and
// between a question and her answer). Measured from this machine, a Talk took 1.6 to 3.1 seconds
// to open: the key about one, OpenAI's answer to the offer half to one, and its handshake about
// 0.9 that nothing here can shorten. So, while the panel is open (`prepare`), the key is minted
// ahead and a request that carries nothing warms the connection to OpenAI (0.3 seconds off the
// offer, still warm 90 seconds later); on Talk, the microphone and the offer start at once instead
// of after the key. And `ask_vera` hands the model kadmon's answer the moment the find has written
// it (`answer` on the stream), while its matches are still being written for the screen — a yes
// to a card waits for that ask to finish, since the card's id comes last.
//
// THE SAME STORE SHAPE AS THE LOCAL LOOP (src/voice-mode.ts): the panel and its stage draw either
// through `VoiceModeStore`, and `/me`'s `voiceEnabled` — since 23 Sep 2026 with `voiceVendor`,
// which also names ElevenLabs' conversation (elevenlabs.ts) — says which one Talk opens. The pure
// part — events in, state and tool calls out — is `createRealtimeVoice`, driven by
// realtime.test.ts on a fake link; `browserRealtimeLink` is the WebRTC adapter, thin and untested
// like `browserSocket`.
import type { OpenAiVoiceSession } from '@vera/bff';
import { streamMeter, type LevelMeter } from './audio-level.ts';
import type { OrbLevels } from './orb.ts';
import { explainSpeechError } from './speech.ts';
import type { VoiceCallbacks, VoiceModeStore, VoiceSnapshot } from './voice-mode.ts';
import { createVoiceTools } from './voice-tools.ts';

// The tools' answers and the second yes are voice-tools.ts's, shared with ElevenLabs (23 Sep 2026).

/** The stage's line on who hears the recruiter — set in capitals, so "our" not "kadmon's". */
export const OPENAI_HEARS =
  "OpenAI hears you and speaks for kadmon; kadmon's answers are Claude's, and every question and answer is in this chat";

/** How long the connection may take to open before the mode ends with a note. */
export const CONNECT_TIMEOUT_MS = 12_000;

/**
 * A key minted ahead is handed to a call only while it has this long left: the call needs it for
 * a second or two, and a key minted at the edge of its ten minutes is not worth the risk.
 */
export const KEY_MARGIN_MS = 90_000;

/**
 * The least time between two warm-ups of the connection to OpenAI: the panel asks on every open and
 * after every conversation, and a warmed connection measured still warm at 90 s. Nothing warms it
 * again on a timer while the panel stays open.
 */
export const WARM_EVERY_MS = 60_000;

/** One event, either way, as the data channel carries it. */
export interface RealtimeEvent {
  type: string;
  [key: string]: unknown;
}

export interface RealtimeLinkHandlers {
  onEvent(event: RealtimeEvent): void;
  /** The call ended from the far side, or the connection dropped. */
  onClose(reason: string | null): void;
}

/** The open call: events out, the microphone's switch, and the hang-up. */
export interface RealtimeLink {
  send(event: RealtimeEvent): void;
  mute(muted: boolean): void;
  close(): void;
  /**
   * How loud the recruiter and kadmon are this instant, for the orb (orb.ts, 16 Sep 2026); null
   * where the browser gives no reading.
   */
  levels?(): OrbLevels | null;
}

/**
 * Opens the call. The key is a promise because it may still be being minted: the microphone and
 * the offer do not wait for it (16 Sep 2026).
 */
export type OpenRealtimeLink = (
  session: Promise<OpenAiVoiceSession>,
  handlers: RealtimeLinkHandlers,
) => Promise<RealtimeLink>;

export interface RealtimeParts {
  /** The key for one call, minted for this recruiter (api/voice.ts). */
  session(): Promise<OpenAiVoiceSession>;
  open: OpenRealtimeLink;
  /** Warms the connection to where the call's offer goes, before Talk (`browserWarmUp`). */
  warm?(callsUrl: string): void;
  /** T449: a line for the harness's log (voice-tools.ts `trace`); the panel passes none. */
  trace?(what: string): void;
}

/** A function call as `response.done` carries it in `response.output`. */
interface FunctionCallItem {
  type: 'function_call';
  name: string;
  call_id: string;
  arguments: string;
}

const isFunctionCall = (item: unknown): item is FunctionCallItem =>
  typeof item === 'object' &&
  item !== null &&
  (item as { type?: unknown }).type === 'function_call' &&
  typeof (item as { name?: unknown }).name === 'string' &&
  typeof (item as { call_id?: unknown }).call_id === 'string';

/** Whether an output item of `response.done` carries audio she speaks (`output_audio`, or the beta's `audio`). */
const hasAudio = (item: unknown): boolean => {
  const content = (item as { content?: unknown } | null)?.content;
  return (
    Array.isArray(content) &&
    content.some((part) => {
      const type = (part as { type?: unknown } | null)?.type;
      return type === 'output_audio' || type === 'audio';
    })
  );
};

export function createRealtimeVoice(
  parts: RealtimeParts,
  now: () => number = Date.now,
): VoiceModeStore {
  let snap: VoiceSnapshot = {
    state: 'off',
    heard: '',
    said: '',
    muted: false,
    note: null,
    card: null,
    showing: false,
  };
  let callbacks: VoiceCallbacks | null = null;
  let link: RealtimeLink | null = null;
  // Bumped by start and end: anything from before the bump — a late connection, a late tool
  // result — finds it changed and does nothing.
  let generation = 0;
  const listeners = new Set<() => void>();

  // The key minted ahead of Talk, unused; and one being minted. A key opens one call, so a call
  // takes it and nothing else ever sees it again.
  let ready: OpenAiVoiceSession | null = null;
  let minting: Promise<OpenAiVoiceSession> | null = null;
  const fresh = (session: OpenAiVoiceSession) => session.expiresAt * 1000 - now() > KEY_MARGIN_MS;
  // The panel remounts on every chat it opens; one warm-up a minute is plenty.
  let warmedAt = -Infinity;
  const warm = (callsUrl: string) => {
    if (!parts.warm || now() - warmedAt < WARM_EVERY_MS) return;
    warmedAt = now();
    parts.warm(callsUrl);
  };

  const mintAhead = () => {
    const pending = parts.session();
    minting = pending;
    pending.then(
      (session) => {
        if (minting !== pending) return; // a Talk took it while it was being minted
        minting = null;
        ready = session;
        warm(session.callsUrl);
      },
      () => {
        // Ahead of time, a failed mint is nobody's error: Talk mints again, and says why if that
        // fails too.
        if (minting === pending) minting = null;
      },
    );
  };

  /** The key for the call opening now: the one minted ahead while it is good, else a new one. */
  const takeKey = (): Promise<OpenAiVoiceSession> => {
    const kept = ready;
    ready = null;
    if (kept && fresh(kept)) return Promise.resolve(kept);
    if (minting) {
      const pending = minting;
      minting = null;
      return pending;
    }
    return parts.session();
  };

  const set = (patch: Partial<VoiceSnapshot>) => {
    snap = { ...snap, ...patch };
    for (const listener of listeners) listener();
  };

  const end = (note: string | null) => {
    generation += 1;
    tools.reset();
    // No test can see these two reset: the first `response.created` of the next conversation sets
    // `repliedTurns` from `heardTurns` again, and nothing reads the pair before it (T442a).
    heardTurns = 0;
    repliedTurns = 0;
    link?.close();
    link = null;
    set({ state: 'off', heard: '', said: '', note, card: null, showing: false });
  };

  /** The mic's state after she has finished: listening, unless the recruiter muted it. */
  const rest = () => set({ state: snap.muted ? 'muted' : 'listening' });

  const send = (event: RealtimeEvent) => link?.send(event);

  // The three tools, and the second yes a careful card needs — voice-tools.ts, shared.
  // T436: which of the recruiter's turns she has begun a response to, so a correction can tell
  // from these events (not a sampled state) that she has replied and finished saying it.
  let heardTurns = 0;
  let repliedTurns = 0;
  // T449: a result or a told line now reaches her whenever it is ready, so it can land inside a
  // response of hers; OpenAI refuses a second `response.create` while one is active, so one waits
  // for `response.done` (the vendor's event, no clock).
  let responding = false;
  let respondWhenDone = false;
  const respond = () => {
    if (responding) {
      respondWhenDone = true;
      return;
    }
    responding = true;
    send({ type: 'response.create' });
  };
  const tools = createVoiceTools({
    callbacks: () => callbacks,
    herReplyDone: () =>
      repliedTurns === heardTurns && (snap.state === 'listening' || snap.state === 'muted'),
    ...(parts.trace ? { trace: parts.trace } : {}),
    snapshot: () => snap,
    set: (patch) => set(patch),
    generation: () => generation,
    // 24 Sep 2026: a correction from the brain, as a line in the conversation she answers aloud.
    tell(line) {
      send({
        type: 'conversation.item.create',
        item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: line }] },
      });
      respond();
    },
  });

  /** One tool call, answered from the panel's callbacks; the model continues on the result. */
  const runTool = async (call: FunctionCallItem) => {
    set({ state: 'thinking' });
    let args: Record<string, unknown> = {};
    try {
      args = JSON.parse(call.arguments || '{}') as Record<string, unknown>;
    } catch {
      args = {};
    }
    const output = await tools.run(call.name, args);
    if (output === null) return;
    // T436a: an answer comes back after the recruiter spoke again, so the state reads listening;
    // a correction waiting on that state would count the gap before `response.created` as her being
    // quiet. She is about to answer: say so now.
    if (snap.state === 'listening' || snap.state === 'muted') set({ state: 'thinking' });
    send({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: call.call_id, output: JSON.stringify(output) },
    });
    respond();
  };

  const onEvent = (event: RealtimeEvent) => {
    if (snap.state === 'off') return;
    switch (event.type) {
      case 'input_audio_buffer.speech_started':
        // The recruiter is talking — over her, if she was speaking; the model stops itself.
        tools.heard();
        heardTurns += 1;
        set({ state: snap.muted ? 'muted' : 'listening', heard: '', said: '' });
        break;
      case 'conversation.item.input_audio_transcription.completed':
        set({ heard: String(event.transcript ?? '') });
        break;
      case 'response.created':
        // Any response counts, a correction's own too: it is her reply to every turn heard so far,
        // so it frees nothing for a recruiter who speaks after it (realtime.test.ts). The race that
        // stays: they speak inside the round trip of a correction's `response.create`, and that
        // response's `response.created` then reads as her reply to what they just said (T442a).
        repliedTurns = heardTurns;
        responding = true;
        set({ state: 'thinking', said: '' });
        break;
      case 'output_audio_buffer.started':
        set({ state: 'speaking' });
        break;
      case 'response.output_audio_transcript.delta':
        set({ state: 'speaking', said: `${snap.said}${String(event.delta ?? '')}` });
        break;
      case 'response.output_audio_transcript.done':
        set({ said: String(event.transcript ?? snap.said) });
        tools.spoke(); // T449: a reply of hers heard, what a correction to an answer waits on
        break;
      case 'response.done': {
        responding = false;
        const output = (event.response as { output?: unknown[] } | undefined)?.output ?? [];
        const calls = output.filter(isFunctionCall);
        // T436a: a response with no audio and no call (empty, cancelled) would leave `thinking` on
        // for good; the state goes back to listening.
        if (calls.length === 0 && !output.some(hasAudio) && snap.state === 'thinking') rest();
        if (calls.length > 0) {
          // One at a time, in order: two cards proposed at once would race on their own order.
          void (async () => {
            for (const call of calls) await runTool(call);
          })();
        }
        if (respondWhenDone) {
          respondWhenDone = false;
          respond();
        }
        break;
      }
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
        if (snap.state === 'speaking') rest();
        break;
      case 'error': {
        const message = (event.error as { message?: unknown } | undefined)?.message;
        set({ note: typeof message === 'string' ? message : 'OpenAI reported an error.' });
        break;
      }
      default:
        break;
    }
  };

  return {
    snapshot: () => snap,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    bind(next) {
      callbacks = next;
    },
    start() {
      if (snap.state !== 'off') return;
      generation += 1;
      // As in `end`: a reset no test can tell from none (T442a).
      heardTurns = 0;
      repliedTurns = 0;
      responding = false;
      respondWhenDone = false;
      const mine = generation;
      set({
        state: 'connecting',
        heard: '',
        said: '',
        note: null,
        card: null,
        showing: false,
        muted: false,
      });
      const key = takeKey();
      // The link reads the key; a microphone refused before it does must not leave the key's own
      // failure unhandled.
      key.catch(() => {});
      const opening = parts.open(key, {
        onEvent: (event) => {
          if (mine === generation) onEvent(event);
        },
        onClose: (reason) => {
          if (mine === generation) end(reason ?? 'The voice connection closed.');
        },
      });
      void (async () => {
        try {
          const opened = await Promise.race([
            opening,
            new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error('timeout')), CONNECT_TIMEOUT_MS);
            }),
          ]);
          if (mine !== generation) {
            opened.close();
            return;
          }
          link = opened;
          set({ state: 'listening' });
        } catch (thrown) {
          // A call that opens after the mode gave up on it, or was ended, is hung up at once — or
          // the microphone would stay open on a call nobody is in.
          opening.then(
            (late) => {
              if (link !== late) late.close();
            },
            () => {},
          );
          if (mine !== generation) return;
          end(explainConnectFailure(thrown));
        }
      })();
    },
    prepare() {
      if (snap.state !== 'off') return;
      if (ready && fresh(ready)) {
        warm(ready.callsUrl);
        return;
      }
      if (!minting) mintAhead();
    },
    end: () => end(null),
    levels: () => (link?.levels ? link.levels() : null),
    toggleMute() {
      const muted = !snap.muted;
      set({ muted });
      link?.mute(muted);
      if (muted && snap.state === 'listening') set({ state: 'muted' });
      else if (!muted && snap.state === 'muted') set({ state: 'listening' });
    },
    interrupt() {
      if (snap.state !== 'speaking' && snap.state !== 'thinking') return;
      send({ type: 'response.cancel' });
      send({ type: 'output_audio_buffer.clear' });
      set({ said: '' });
      rest();
    },
  };
}

/** Why the conversation could not open, in a sentence a recruiter can act on. */
export function explainConnectFailure(thrown: unknown): string {
  const code = (thrown as { code?: unknown } | null)?.code;
  if (code === 'voice_unavailable') {
    return 'The voice is not set up on this server. Type instead, or use the mic.';
  }
  if (code === 'network' || code === 'no_token') {
    return 'The brain API did not answer, so the conversation could not open.';
  }
  const name = (thrown as { name?: unknown } | null)?.name;
  if (name === 'NotAllowedError' || name === 'NotFoundError') {
    return explainSpeechError(name === 'NotFoundError' ? 'audio-capture' : 'not-allowed') ?? '';
  }
  const message = (thrown as { message?: unknown } | null)?.message;
  return message === 'timeout'
    ? 'The voice connection did not open in time. Try again.'
    : `The conversation could not open${typeof message === 'string' ? `: ${message}` : ''}.`;
}

/**
 * The WebRTC call, as OpenAI's guide has it: the microphone as the one outgoing track, her voice
 * on the one incoming track played through an audio element, and the events on the "oai-events"
 * data channel. Null where the browser has no WebRTC or no microphone API.
 */
export function browserRealtimeLink(scope: object): OpenRealtimeLink | null {
  const {
    RTCPeerConnection: PeerConnection,
    Audio: AudioElement,
    navigator: nav,
    fetch: fetchImpl,
  } = scope as {
    RTCPeerConnection?: typeof RTCPeerConnection;
    Audio?: typeof Audio;
    navigator?: Navigator;
    fetch?: typeof fetch;
  };
  if (!PeerConnection || !AudioElement || !nav?.mediaDevices?.getUserMedia || !fetchImpl)
    return null;

  return async (session, handlers) => {
    // The orb's ears (16 Sep 2026): made here, before the first await, while the tap on Talk still
    // counts as the user's gesture — a browser starts audio only inside one. None where it cannot.
    const meters = createMeterAudio(scope);
    // The microphone and the offer first, while the key may still be being minted.
    let stream: MediaStream;
    try {
      stream = await nav.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch (error) {
      void meters?.close();
      throw error;
    }
    const track = stream.getAudioTracks()[0];
    if (!track) {
      for (const each of stream.getTracks()) each.stop();
      void meters?.close();
      throw new Error('no microphone track');
    }
    const pc = new PeerConnection();
    const audio = new AudioElement();
    audio.autoplay = true;
    const heard: LevelMeter | null = meters ? meterOf(meters, stream) : null;
    let said: LevelMeter | null = null;
    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      track.stop();
      pc.close();
      audio.srcObject = null;
      heard?.close();
      said?.close();
      void meters?.close();
    };
    try {
      pc.ontrack = (event) => {
        const incoming = event.streams[0] ?? null;
        audio.srcObject = incoming;
        void audio.play().catch(() => {});
        if (meters && incoming && !said) said = meterOf(meters, incoming);
      };
      pc.addTrack(track, stream);
      const channel = pc.createDataChannel('oai-events');
      channel.onmessage = (event: MessageEvent<string>) => {
        try {
          handlers.onEvent(JSON.parse(event.data) as RealtimeEvent);
        } catch {
          // Not an event: ignored.
        }
      };
      channel.onclose = () => {
        if (!closed) {
          close();
          handlers.onClose(null);
        }
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const key = await session;
      const response = await fetchImpl.call(
        scope,
        `${key.callsUrl}?model=${encodeURIComponent(key.model)}`,
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${key.clientSecret}`,
            'content-type': 'application/sdp',
          },
          body: offer.sdp ?? '',
        },
      );
      if (!response.ok) throw new Error(`OpenAI refused the call (${response.status})`);
      await pc.setRemoteDescription({ type: 'answer', sdp: await response.text() });
      await new Promise<void>((resolve, reject) => {
        if (channel.readyState === 'open') {
          resolve();
          return;
        }
        channel.onopen = () => resolve();
        channel.onerror = () => reject(new Error('the voice connection failed'));
      });
      return {
        send: (event) => {
          if (channel.readyState === 'open') channel.send(JSON.stringify(event));
        },
        mute: (muted) => {
          track.enabled = !muted;
        },
        close,
        levels: () =>
          heard || said ? { input: heard?.level() ?? 0, output: said?.level() ?? 0 } : null,
      };
    } catch (error) {
      // Whatever failed — the key, OpenAI, the handshake — the microphone does not stay open.
      close();
      throw error;
    }
  };
}

/** The browser's Web Audio for the orb's meters, running; null where there is none or it will not start. */
function createMeterAudio(scope: object): AudioContext | null {
  const { AudioContext: Context, webkitAudioContext: Webkit } = scope as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const Made = Context ?? Webkit;
  if (!Made) return null;
  try {
    const context = new Made();
    if (context.state === 'suspended') void context.resume().catch(() => {});
    return context;
  } catch {
    return null;
  }
}

/** A meter on a stream, or none where this browser will not analyse it: the orb falls back. */
function meterOf(context: AudioContext, stream: MediaStream): LevelMeter | null {
  try {
    return streamMeter(context, stream);
  } catch {
    return null;
  }
}

/**
 * Warms the connection to OpenAI before a Talk (16 Sep 2026): a HEAD to where the call's offer
 * will go, with no key, no cookie and nothing read back, so the offer finds DNS and TLS already
 * done — 0.3 seconds, measured, and still warm after 90. It tells OpenAI no more than the call's
 * own request will; and it is made only where the brain's voice is on OpenAI and the panel is open.
 */
export function browserWarmUp(scope: object): ((callsUrl: string) => void) | null {
  const { fetch: fetchImpl } = scope as { fetch?: typeof fetch };
  if (!fetchImpl) return null;
  return (callsUrl) => {
    void fetchImpl
      .call(scope, callsUrl, { method: 'HEAD', mode: 'no-cors', credentials: 'omit' })
      .catch(() => {});
  };
}
